import every from 'lodash/every';
import compact from 'lodash/compact';
import { firstValueFrom, from } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import Breadcrumb from '@Services/Breadcrumb';
import store from '@Store';
import { commaFormatNumber } from '@Utils/String';
import UserDatabases from '@Services/UserDatabases';
import { mapContextToTitle } from '@Containers/ResultToolbar/ResultToolbarConstants';
import { rootBreadcrumbForDb as rootBreadcrumbForDbSelector } from '@Containers/Breadcrumbs/BreadcrumbsSelectors';
import { recordCountOrTotalResultFromDatabaseItem } from '@Containers/PatentFamilyClustering/selectors';

const requireKeys = ['sort', 'order'];

const isResultsParamsReady = (params) => every(requireKeys, (key) => !!params[key]);

const getActiveBreadcrumb = (params) => Breadcrumb.getBreadcrumbForDbByResultName(params.databaseId, params.resultName);

const getRootBreadcrumb = () => {
  const rootBreadcrumb = rootBreadcrumbForDbSelector(store.getState());

  if (rootBreadcrumb) {
    return rootBreadcrumb;
  }

  return firstValueFrom(from(store).pipe(filter(rootBreadcrumbForDbSelector), map(rootBreadcrumbForDbSelector), take(1)));
};

const getQueryString = (rootBreadcrumb) => {
  const historyType = rootBreadcrumb.get('historyType');

  if (historyType === 'quickSearch') {
    const quickSearchItem = rootBreadcrumb.get('quickSearchItem');
    const content = quickSearchItem.get('content');
    const structure = quickSearchItem.get('structure');
    const structureStr = structure ? 'structure' : undefined;
    return compact([`"${content}"`, structureStr]).join(' AND ');
  }

  return rootBreadcrumb.get('comment');
};

const getFilteredBy = (activeBreadcrumb) => {
  const historyType = activeBreadcrumb?.historyType;

  if (historyType === 'filtered') {
    const { filters } = activeBreadcrumb;
    return `Filtered by ${filters.map((filterItem) => filterItem.title).join(', ')}`;
  }

  return undefined;
};

const getDatabaseName = (activeBreadcrumb) => UserDatabases.getDbName(activeBreadcrumb?.databaseId);

export async function resultsMapper(params) {
  const isResultsReady = isResultsParamsReady(params);
  // eslint-disable-next-line prefer-promise-reject-errors
  if (!isResultsReady) return Promise.reject('Results params not ready');

  const rootBreadcrumb = await getRootBreadcrumb();
  const activeBreadcrumb = getActiveBreadcrumb(params);
  const totalResult = recordCountOrTotalResultFromDatabaseItem(activeBreadcrumb);
  const context = activeBreadcrumb?.context;
  const formattedResultAmount = `${commaFormatNumber(totalResult)} ${mapContextToTitle[context]}`;
  const queryString = getQueryString(rootBreadcrumb);
  const filteredBy = getFilteredBy(activeBreadcrumb);
  const databaseName = getDatabaseName(activeBreadcrumb);

  return {
    title: `${formattedResultAmount} for ${compact([queryString, filteredBy]).join(' ')} in ${databaseName}`,
    pageName: filteredBy ? 'Filtered search results' : 'Search results',
  };
}

export async function heatmapMapper() {
  const rootBreadcrumb = await getRootBreadcrumb();
  const queryString = getQueryString(rootBreadcrumb);

  return {
    title: `Bioactivity Visualization for ${queryString}`,
    pageName: 'Search results',
  };
}
